import { usePathname, type Href } from 'expo-router';
import { NativeTabs } from 'expo-router/unstable-native-tabs';
import { HomeIcon, RouteIcon, ShieldIcon, UserIcon } from 'lucide-react-native';
import * as React from 'react';
import { Platform, useWindowDimensions, View } from 'react-native';

import { DashboardSidebar, type SidebarItem } from '@/components/dashboard-sidebar';
import { useSession } from '@/components/session-provider';
import { useTenant } from '@/components/tenant-provider';
import { Text } from '@sixam/ui/text';
import { BRAND } from '@sixam/config/brand';
import { useReleaseFeatureFlag } from '@/lib/use-release-feature-flag';

export default function GlobalAppTabsLayout() {
  const pathname = usePathname();
  const { height, width } = useWindowDimensions();
  const { isAdmin, profile, session } = useSession();
  const { activeTenant, isAnyTenantAdmin } = useTenant();
  const routeLibraryEnabled = useReleaseFeatureFlag('routeLibrary') === true;
  const avatarUrl =
    profile?.avatar_url ?? (session?.user.user_metadata.avatar_url as string | undefined);
  const avatarSource = React.useMemo(
    () => (avatarUrl ? { uri: avatarUrl } : undefined),
    [avatarUrl]
  );
  const isRouteEditor = pathname === '/app/routes/new';
  const useSidebarLayout =
    !isRouteEditor && Platform.OS === 'ios' && Platform.isPad && width > height && width >= 900;

  const items: SidebarItem[] = [
    {
      label: 'Home',
      icon: HomeIcon,
      href: '/app/home' as Href,
      active: pathname === '/app' || pathname.startsWith('/app/home'),
    },
    {
      label: 'Me',
      icon: UserIcon,
      href: '/app/me' as Href,
      active: pathname.startsWith('/app/me'),
    },
  ];
  if (routeLibraryEnabled) {
    items.splice(1, 0, {
      label: 'Routes',
      icon: RouteIcon,
      href: '/app/routes' as Href,
      active: pathname.startsWith('/app/routes'),
    });
  }

  if (isAdmin || isAnyTenantAdmin) {
    items.push({
      label: 'Admin',
      icon: ShieldIcon,
      href: '/admin' as Href,
      active: pathname.startsWith('/admin'),
    });
  }

  return (
    <View className={useSidebarLayout ? 'flex-1 flex-row bg-background' : 'flex-1 bg-background'}>
      {useSidebarLayout ? (
        <DashboardSidebar
          brand="6am BKK"
          subtitle="Member"
          items={items}
          footer={
            <Text variant="muted" className="flex-1 text-xs" numberOfLines={1}>
              {activeTenant?.name ?? 'All clubs'}
            </Text>
          }
        />
      ) : null}
      <View className="min-w-0 flex-1">
        <NativeTabs
          tintColor={BRAND.color.orange}
          minimizeBehavior="onScrollDown"
          sidebarAdaptable={false}
          hidden={isRouteEditor || useSidebarLayout}
        >
          <NativeTabs.Trigger name="home">
            <NativeTabs.Trigger.Label>Home</NativeTabs.Trigger.Label>
            <NativeTabs.Trigger.Icon sf="house.fill" md="home" />
          </NativeTabs.Trigger>

          {routeLibraryEnabled ? (
            <NativeTabs.Trigger name="routes">
              <NativeTabs.Trigger.Label>Routes</NativeTabs.Trigger.Label>
              <NativeTabs.Trigger.Icon
                sf="point.bottomleft.forward.to.point.topright.scurvepath"
                md="route"
              />
            </NativeTabs.Trigger>
          ) : null}

          <NativeTabs.Trigger name="me">
            <NativeTabs.Trigger.Label>Me</NativeTabs.Trigger.Label>
            {avatarSource ? (
              <NativeTabs.Trigger.Icon src={avatarSource} renderingMode="original" />
            ) : (
              <NativeTabs.Trigger.Icon sf="person.crop.circle" md="account_circle" />
            )}
          </NativeTabs.Trigger>
        </NativeTabs>
      </View>
    </View>
  );
}
